<template>
  <CardLayout v-bind="clickable ? { onClick: () => $emit('click') } : {}">
    <template #header>
      {{ $t('report.client_types_title') }}
    </template>
    <div v-if="loading">
      <Loading />
    </div>
    <div v-else-if="clientTypeKeys.length > 0" class="w-full space-y-4">
      <!-- Revenue proportion bar -->
      <div v-if="proportionData" class="w-full">
        <span class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {{ $t('report.revenue_split') }}
        </span>
        <div class="flex w-full h-[6px] rounded-full overflow-hidden mt-1.5">
          <div v-for="item in proportionData" :key="item.name"
            class="h-full transition-all duration-500"
            :style="{ width: item.percentage + '%', backgroundColor: item.color }">
          </div>
        </div>
      </div>

      <!-- Two-column B2B / B2C -->
      <div class="grid grid-cols-2 gap-4">
        <div v-for="type in clientTypeKeys" :key="type" class="flex flex-col">
          <div class="flex items-center gap-1.5 mb-2">
            <div class="w-1.5 h-1.5 rounded-full"
              :style="{ backgroundColor: type === 'B2B' ? '#F59E0B' : '#06C8FF' }"></div>
            <span class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              {{ type }}
              <span v-if="proportionData" class="font-semibold"
                :style="{ color: type === 'B2B' ? '#F59E0B' : '#06C8FF' }">
                · {{ proportionData.find(p => p.name === type)?.percentage.toFixed(0) }}%
              </span>
            </span>
          </div>
          <span class="text-[16px] font-bold text-foreground leading-tight">
            {{ formatClientTypeAmount(getClientTypeTotal(type)) }}
          </span>
          <span v-if="getClientTypeDocCount(type) > 0"
            class="text-[11px] text-muted-foreground mt-1">
            {{ getClientTypeDocCount(type) }} {{ $t('report.transactions') }}
          </span>
        </div>
      </div>
    </div>
    <div v-else class="w-full flex flex-col items-center justify-center py-6">
      <p class="text-sm text-muted-foreground">{{ $t('data.unavailable') }}</p>
    </div>
  </CardLayout>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { SalesByClientTypesResponse, TaxDataKey } from '../../responses'
import type { Center } from '../../user'
import { formatCurrencyForCenter } from '../../utils/Languages'
import CardLayout from '../CardLayout.vue'
import Loading from '../Loading.vue'

const props = withDefaults(defineProps<{
  clientTypesReport: SalesByClientTypesResponse | null | undefined
  loading: boolean
  taxKey: TaxDataKey
  center: Center | undefined
  clickable?: boolean
}>(), {
  clickable: true,
})

defineEmits<{
  click: []
}>()

const clientTypeKeys = computed<string[]>(() => {
  if (!props.clientTypesReport?.client_types) return []
  return Object.keys(props.clientTypesReport.client_types)
})

function getClientTypeTotal(type: string): number {
  const data = props.clientTypesReport?.client_types[type]
  if (!data?.intervals?.length) return 0
  const lastInterval = data.intervals[data.intervals.length - 1]
  return lastInterval.data.cumulative.tax_data[props.taxKey] || 0
}

function formatClientTypeAmount(amount: number): string {
  return formatCurrencyForCenter(amount, props.center)
}

function getClientTypeDocCount(type: string): number {
  const data = props.clientTypesReport?.client_types[type]
  if (!data?.intervals?.length) return 0
  const lastInterval = data.intervals[data.intervals.length - 1]
  return lastInterval.data.cumulative.total_documents || 0
}

const proportionData = computed(() => {
  if (clientTypeKeys.value.length === 0) return null
  const totals: Record<string, number> = {}
  let grandTotal = 0
  for (const key of clientTypeKeys.value) {
    const total = getClientTypeTotal(key)
    totals[key] = total
    grandTotal += total
  }
  if (grandTotal === 0) return null
  return clientTypeKeys.value.map((key) => ({
    name: key,
    percentage: (totals[key] / grandTotal) * 100,
    color: key === 'B2B' ? '#F59E0B' : '#06C8FF'
  }))
})
</script>
